import { Fragment } from 'react'
import { Link } from 'react-router'

import { CallActivityCard } from '@/components/Aircall/CallActivityCard'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { formatDateTime } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { SalesLeadActivityType } from '@/models/salesLead'
import { type StaffActivity } from '@/models/staffUser'
import { ActivityAuthorIcon } from '../SalesLeads/ActivityAuthorIcon'
import { ActivityContent } from '../SalesLeads/ActivityContent'
import { activityMetaParts } from '../SalesLeads/activityMeta'
import { activityTypeStyles } from '../SalesLeads/activityTypeStyles'

// The lead timeline's call types; a legacy plain CALL has no call event to play.
const CALL_TYPES = new Set<SalesLeadActivityType>([
  SalesLeadActivityType.CALL_INBOUND,
  SalesLeadActivityType.CALL_OUTBOUND,
  SalesLeadActivityType.CALL_INBOUND_MISSED,
  SalesLeadActivityType.CALL_OUTBOUND_MISSED,
])

/** One feed item: the lead it happened on, then the activity as the lead's timeline shows it. */
export function StaffActivityItem({ activity }: { activity: StaffActivity }) {
  const style = activityTypeStyles[activity.activityType]
  const Icon = style.icon

  return (
    <div className="flex flex-col gap-1.5">
      <Link
        to={`/insure/internal/sales-leads/${activity.lead.id}#activity-${activity.id}`}
        className="w-fit text-sm font-medium text-blue-600 underline-offset-2 hover:underline"
      >
        {activity.lead.name}
      </Link>
      {CALL_TYPES.has(activity.activityType) ? (
        <CallActivityCard leadId={activity.lead.id} activity={activity} />
      ) : (
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={cn('gap-1 px-2 py-0.5', style.className)}>
                <Icon className="size-3" />
                {style.label}
              </Badge>
              <span className="text-muted-foreground flex items-center text-sm">
                <ActivityAuthorIcon activity={activity} />
                <span>
                  {activity.createdByName || 'System'} &middot; {formatDateTime(activity.createdAt)}
                  {activityMetaParts(activity).map((part) => (
                    <Fragment key={part}> &middot; {part}</Fragment>
                  ))}
                  {activity.smsStatus === 'failed' && (
                    <>
                      {' '}
                      &middot; <span className="text-red-600">failed</span>
                    </>
                  )}
                </span>
              </span>
            </div>
            <ActivityContent activity={activity} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}
